import EditIcon from "@mui/icons-material/Edit"
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { DropzoneArea } from "mui-file-dropzone"
import { useEffect, useState } from "react"
import chapterService from "services/chapterService"
import trainingModuleService from "services/trainingModuleService"
import { setChapters, updateChapter } from "slices/chapterSlice"
import type { IChapter, IUpdateChapter } from "types/chapterTypes"
import type { ITrainingModule } from "types/trainingModuleTypes"
import * as Yup from "yup"

type Props = {
  chapter: IChapter
}

export function UpdateChapterDialog({ chapter }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [trainingModules, setTrainingModules] = useState<Array<ITrainingModule>>([])

  const dispatch = useAppDispatch()

  useEffect(() => {
    trainingModuleService
      .getTrainingModules()
      .then((resp) => {
        setTrainingModules(resp)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])

  const currentSimulator = useAppSelector(
    (state) => state.simulatorapplication.currentSimulatorApplication,
  )

  const initialValues: IUpdateChapter = {
    name: chapter.name,
    description: chapter.description,
    imageFile: undefined,
    trainingModuleId: chapter.trainingModuleId,
  }

  const updateChapterValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter chapter name")
      .max(64, "Maximum name length is 64 characters"),
    description: Yup.string()
      .required("Please enter chapter description")
      .max(256, "Maximum description length is 256 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateChapterValidationScheme,
    onSubmit: (values) => {
      if (currentSimulator === undefined) {
        return
      }

      setIsLoading(true)

      const chapterUpdate: IUpdateChapter = {
        name: values.name,
        description: values.description,
        imageFile: values.imageFile,
        trainingModuleId: values.trainingModuleId,
      }
      chapterService
        .updateChapter(chapter.id, chapterUpdate)
        .then((resp) => {
          dispatch(updateChapter(resp))
        })
        .then(() => chapterService.getTrainingModuleChapters(initialValues.trainingModuleId)) //need to update training modules after moving a chapter to another here
        .then((resp2) => {
          dispatch(setChapters(resp2))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Update Chapter</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="module-id">Training Module *</InputLabel>
                  <Select
                    id="trainingModuleId"
                    labelId="module-id"
                    label="Training Module *"
                    name="trainingModuleId"
                    value={formik.values.trainingModuleId}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.trainingModuleId && Boolean(formik.errors.trainingModuleId)
                    }
                  >
                    {trainingModules.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="description"
                  label="Description"
                  type="description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={(files) => {
                    formik.setFieldValue("imageFile", files[0])
                  }}
                  filesLimit={1}
                  fileObjects={formik.values.imageFile}
                  acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
